import React, { useState, useEffect } from "react";
import styles from "./News.module.scss";
import backPagination from "../../assets/images/backPagination.png";
import nextPagination from "../../assets/images/nextPagination.png";
import BreadCrumb from "../CommonComponents/BreadCrumb.tsx";
import classNames from "classnames";
import Modal from "react-bootstrap/Modal";
import { motion } from "framer-motion";

import { mediaNewsData } from "../../redux/slices/mediaSlice.js";
import { useDispatch } from "react-redux";

const News: React.FC = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [newsData, setNewsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 12;
  const totalPages = Math.ceil(newsData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchNewseData = async () => {
      setLoading(true);
      try {
        const data = await dispatch(mediaNewsData()).unwrap();
        setNewsData(data?.NewsMedia || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching investor overview data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNewseData();
  }, [dispatch]);

  const paginatedData = newsData?.slice(startIndex, endIndex);

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage("");
  };

  const createPagination = () => {
    let str: JSX.Element[] = [];
    let pageCutLow = currentPage - 1;
    let pageCutHigh = currentPage + 1;

    if (currentPage > 1) {
      str.push(
        <li key="prev" className={`${styles.pageItem} ${styles.previous}`}>
          <a onClick={() => setCurrentPage(currentPage - 1)}>
            <img src={backPagination} alt="backPagination" />
          </a>
        </li>
      );
    }

    if (totalPages < 6) {
      for (let p = 1; p <= totalPages; p++) {
        str.push(
          <li
            key={p}
            className={`${styles.pageItem} ${
              currentPage === p ? styles.active : ""
            }`}
          >
            <a onClick={() => setCurrentPage(p)}>{p}</a>
          </li>
        );
      }
    } else {
      if (currentPage > 2) {
        str.push(
          <li key="first" className={styles.pageItem}>
            <a onClick={() => setCurrentPage(1)}>1</a>
          </li>
        );
        if (currentPage > 3) {
          str.push(
            <li key="dots-prev" className={styles.outOfRange}>
              <a onClick={() => setCurrentPage(currentPage - 2)}>...</a>
            </li>
          );
        }
      }

      if (currentPage === 1) {
        pageCutHigh += 2;
      } else if (currentPage === 2) {
        pageCutHigh += 1;
      }
      if (currentPage === totalPages) {
        pageCutLow -= 2;
      } else if (currentPage === totalPages - 1) {
        pageCutLow -= 1;
      }

      for (let p = pageCutLow; p <= pageCutHigh; p++) {
        if (p > 0 && p <= totalPages) {
          str.push(
            <li
              key={p}
              className={`${styles.pageItem} ${
                currentPage === p ? styles.active : ""
              }`}
            >
              <a onClick={() => setCurrentPage(p)}>{p}</a>
            </li>
          );
        }
      }

      if (currentPage < totalPages - 1) {
        if (currentPage < totalPages - 2) {
          str.push(
            <li key="dots-next" className={styles.outOfRange}>
              <a onClick={() => setCurrentPage(currentPage + 2)}>...</a>
            </li>
          );
        }
        str.push(
          <li key="last" className={styles.pageItem}>
            <a onClick={() => setCurrentPage(totalPages)}>{totalPages}</a>
          </li>
        );
      }
    }

    if (currentPage < totalPages) {
      str.push(
        <li key="next" className={`${styles.pageItem} ${styles.next}`}>
          <a onClick={() => setCurrentPage(currentPage + 1)}>
            <img src={nextPagination} alt="nextPagination" />
          </a>
        </li>
      );
    }

    return str;
  };
  const breadcrumb = [
    { title: "Home", link: "/" },
    { title: "News & Media", link: "/news-and-media" },
    { title: "Aptech in News", link: "" },
  ];

  const processDescription = (html) => {
    if (/<a[\s\S]*?<\/a>/.test(html)) {
      return html
        .replace(/<a /g, '<a target="_blank" ')
        .replace(/<p>/g, "")
        .replace(/<\/p>/g, "");
    } else {
      let processedHtml = html
        .replace(/<p>/g, "<span>")
        .replace(/<\/p>/g, "</span>");

      processedHtml = `<span style="color: #DB6516;">${processedHtml}</span>`;

      return processedHtml;
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  return (
    <>
      <BreadCrumb menu={breadcrumb} />
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }} // Triggers animation when 50% of the element is visible
        transition={{ duration: 1, ease: "easeInOut" }}
      >
        <div className={`${styles.News} mt-5`}>
          <div className="container">
            <div className="section">
              <div className="row g-0">
                <div className="col-12">
                  <h1 className="heading">Aptech in the News</h1>
                  <p className="para mb-4">
                    <a href="/">Aptech</a> has consistently made headlines
                    globally for its significant contribution in the field of
                    vocational training. Its impactful efforts and
                    accomplishments across diverse sectors of education has made
                    it one of the leading{" "}
                    <a href="/our-businesses/domestic-retail">
                      vocational training institutes
                    </a>{" "}
                    in India and abroad. 
                  </p>
                </div>
              </div>
              {!loading ? (
                <>
                  <div className={styles.newsList}>
                    <div className="row">
                      {paginatedData?.map((card, index) => (
                        <div
                          className={classNames("col-lg-3 col-md-6 col-sm-12")}
                          key={index}
                        >
                          <div className={`mb-5 ${styles.newsCard}`}>
                            <img
                              src={card.main_img}
                              alt="NewsImage"
                              className={styles.images}
                              onClick={() => handleImageClick(card.main_img)}
                            />
                            <div className="mt-3">
                              <p className={`para ${styles.newsHeading}`}>
                                <strong className="">Publication: </strong>
                                {card.publication}
                              </p>
                              <p className={`para ${styles.newsHeading}`}>
                                <strong className="">Dated: </strong>
                                {formatDate(card.date)}
                              </p>
                              <span
                                className={`para ${styles.newsHeading} ${styles.newsHeadingAlign}`}
                              >
                                <strong>Summary/Link:</strong>
                                <span>
                                  <a
                                    href={card.LinkRoute}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    dangerouslySetInnerHTML={{
                                      __html: processDescription(
                                        card.short_description
                                      ),
                                    }}
                                  >
                                    {card.Summary}
                                  </a>
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={styles.pagination}>
                    <ul
                      className={`${styles.ulContainer} ${styles.paginationList}`}
                    >
                      {createPagination()}
                    </ul>
                  </div>
                </>
              ) : (
                <p className="para text-center pt-5">Loading...</p>
              )}
            </div>
          </div>
        </div>
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          className="customModal"
        >
          <div className={`modal-content ${styles["modal-content"]}`}>
            <Modal.Body className={styles.modalContent}>
              <img
                src={selectedImage}
                alt="Selected News"
                className={`${styles.modalImage} p-1`}
              />
            </Modal.Body>
          </div>
        </Modal>
      </motion.div>
    </>
  );
};

export default News;
